/** @jsxImportSource @emotion/react */
import { memo, useState } from "react";
import { EuiButtonIcon, EuiPopover } from "@inscopix/ideas-eui";
import { css } from "@emotion/react";
import { ColumnGroupHeaderBaseName } from "./ColumnGroupHeaderBaseName";
import { ColumnGroupHeaderActionRemoveGroup } from "./actions/ColumnGroupHeaderActionRemoveGroup";
import { ModelDeleteColumnGroup } from "../modals/ModelDeleteColumnGroup";
import { DataTableColumnGroupDefinition } from "../store/DataTableProvider.types";
import { Tooltip } from "components/Tooltip/Tooltip";
import { ModalMapAnalysisParameters } from "../modals/ModalMapAnalysisParameters";
import { ColumnGroupHeaderBaseJoinLink } from "./ColumnGroupHeaderBaseJoinLink";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";

const styles = {
  root: css`
    align-items: center;
    display: flex;
    height: 100%;
    width: 100%;
  `,
  contextMenu: css`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  `,
};

interface ColumnGroupHeaderBaseProps {
  displayName: string;
  tableId: string;
  tableKind: "data" | "analysis" | "workflow";
  columnGroupId: string;
  columnGroupDefinition?: DataTableColumnGroupDefinition;
  suppressMenu?: boolean;
}

/**
 * Component that renders a header for all column group types.
 *
 * All all other header components should render this header and specify props
 * for displaying custom content.
 */
const ColumnGroupHeaderBase = ({
  displayName,
  columnGroupId,
  columnGroupDefinition,
  tableId,
  tableKind,
  suppressMenu,
}: ColumnGroupHeaderBaseProps) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const togglePopover = () => setIsPopoverOpen((isOpen) => !isOpen);
  const closePopover = () => setIsPopoverOpen(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [visibleModal, setVisibleModal] = useState<"mapParams" | "remove">();

  return (
    <div
      css={styles.root}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      {visibleModal === "mapParams" && (
        <ModalMapAnalysisParameters
          tableId={tableId}
          onClose={() => setVisibleModal(undefined)}
        />
      )}

      {visibleModal === "remove" && (
        <ModelDeleteColumnGroup
          tableId={tableId}
          groupId={columnGroupId}
          onClose={() => setVisibleModal(undefined)}
        />
      )}

      <ColumnGroupHeaderBaseName
        tableId={tableId}
        columnGroupId={columnGroupId}
        isRenaming={isRenaming}
        setIsRenaming={setIsRenaming}
        name={displayName}
      />

      {(isMouseOver || isPopoverOpen) &&
        visibleModal === undefined &&
        !suppressMenu && (
          <EuiPopover
            button={
              <EuiButtonIcon
                aria-label="Context menu"
                color="text"
                iconType={() => (
                  <span className="ag-icon ag-icon-menu" unselectable="on" />
                )}
                size="xs"
                onClick={togglePopover}
              />
            }
            isOpen={isPopoverOpen}
            closePopover={closePopover}
            anchorPosition="downLeft"
            panelPaddingSize="none"
            ownFocus={false}
          >
            <div css={styles.contextMenu}>
              <ButtonEmptyPermissioned
                requiredPermission="edit"
                onClick={closePopover}
                iconType="pencil"
                color="text"
                size="s"
                disabled
              >
                Edit
              </ButtonEmptyPermissioned>

              <ButtonEmptyPermissioned
                requiredPermission="edit"
                onClick={() => {
                  closePopover();
                  setIsRenaming(true);
                }}
                iconType="visText"
                color="text"
                size="s"
              >
                Rename column group
              </ButtonEmptyPermissioned>

              {tableKind === "data" && (
                <ColumnGroupHeaderActionRemoveGroup
                  onClick={() => {
                    setVisibleModal("remove");
                    closePopover();
                  }}
                  isDisabled={false}
                />
              )}
            </div>
          </EuiPopover>
        )}

      {columnGroupDefinition?.kind === "join" && !suppressMenu && (
        <ColumnGroupHeaderBaseJoinLink
          groupDefinition={columnGroupDefinition}
        />
      )}

      {columnGroupDefinition?.kind === "sources" && !suppressMenu && (
        <Tooltip content="Map analysis parameters">
          <ButtonIconPermissioned
            requiredPermission="edit"
            aria-label="Map analysis parameters"
            size="xs"
            iconType="indexEdit"
            onClick={() => setVisibleModal("mapParams")}
          />
        </Tooltip>
      )}
    </div>
  );
};

export const ColumnGroupHeaderBaseMemo = memo(ColumnGroupHeaderBase);
